import { Button, Callout, ExternalLink, SectionHeader } from "@octopusdeploy/design-system-components";
import { RunbooksIcon } from "@octopusdeploy/design-system-icons";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import { HasGitPersistenceSettings, HasRunbooksInGit, Permission, Repository } from "@octopusdeploy/octopus-server-client";
import type { ChannelResource, ParentEnvironmentResource, RunbookResource, ProjectResource } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { NotFound } from "~/components/NotFound/NotFound";
import { PageContent } from "~/components/PageContent/PageContent";
import Section from "~/components/Section";
import { ExpandableFormSection, Summary } from "~/components/form";
import { Form, useForm } from "~/components/form/Form/Form";
import SelectWithAddRefresh from "~/components/form/SelectWithAddRefresh/SelectWithAddRefresh";
import { useWorkflow } from "~/workflows/useWorkflow";
import { workflows } from "~/workflows/workflows";
import type { ProjectContextProps } from "../../context";
import { useProjectContext } from "../../context";
import { channelLookupDataLoader, type ChannelLookupData } from "../Channels/CreateOrEditChannelPageContent";
import { ChannelConfigurationCard, ParentEnvironmentConfigurationCard } from "./EphemeralEnvironmentsConfigurationCards";
import { EphemeralEnvironmentChannelInfoPopover, ParentEnvironmentInfoPopover } from "./EphemeralEnvironmentsInfoPopovers";
import { ephemeralEnvironmentsHeader } from "./EphemeralEnvironmentsPage";
import EphemeralEnvironmentsTabs from "./EphemeralEnvironmentsTabs";
import { useEphemeralEnvironmentsEnabled } from "./useEphemeralEnvironmentsEnabled";
export type EphemeralEnvironmentsSettingsPageData = EphemeralEnvironmentsSettingsPageDataNotConfigured | EphemeralEnvironmentsSettingsPageDataConfigured;
interface EphemeralEnvironmentsSettingsPageDataNotConfigured {
    configured: false;
}
interface EphemeralEnvironmentsSettingsPageDataConfigured {
    configured: true;
    runbooks: RunbookResource[];
    channel: ChannelResource;
    channelLookupData: ChannelLookupData;
    parentEnvironment: ParentEnvironmentResource;
}
async function getEphemeralEnvironmentChannel(repository: Repository, project: ProjectResource) {
    const channels = await repository.Channels.allFromProject(project);
    return channels.find((channel) => channel.Type === "EphemeralEnvironment");
}
async function getParentEnvironment(repository: Repository, channel: ChannelResource) {
    if (!channel.ParentEnvironmentId) {
        throw new Error(`Unexpected: Channel '${channel.Name}' (${channel.Id}) does not have a ParentEnvironmentId`);
    }
    return await repository.ParentEnvironments.get({ SpaceId: channel.SpaceId, Id: channel.ParentEnvironmentId });
}
async function getRunbooksForProject(repository: Repository, project: ProjectResource) {
    // Use the default branch from the persistence settings if available, otherwise undefined which will not set a Git ref (and use the database Runbooks endpoint)
    const gitRef = HasGitPersistenceSettings(project.PersistenceSettings) && HasRunbooksInGit(project.PersistenceSettings) ? project.PersistenceSettings.DefaultBranch : undefined;
    const runbooks = await repository.Runbooks.getRunbooks(project, gitRef, { skip: 0, take: Repository.takeAll });
    return runbooks.Items;
}
export async function ephemeralEnvironmentsSettingsPageLoader(repository: Repository, projectContext: Promise<ProjectContextProps>): Promise<EphemeralEnvironmentsSettingsPageData> {
    const { state: { model: project }, } = await projectContext;
    const runbooks = getRunbooksForProject(repository, project);
    const channelLookupData = channelLookupDataLoader(repository, projectContext, true);
    const ephemeralEnvironmentChannel = await getEphemeralEnvironmentChannel(repository, project);
    if (!ephemeralEnvironmentChannel) {
        return {
            configured: false,
        };
    }
    const parentEnvironment = getParentEnvironment(repository, ephemeralEnvironmentChannel);
    return {
        configured: true,
        runbooks: await runbooks,
        channel: ephemeralEnvironmentChannel,
        parentEnvironment: await parentEnvironment,
        channelLookupData: await channelLookupData,
    };
}
interface EphemeralEnvironmentsSettingsPageProps {
    project: ProjectResource;
    loaderData: EphemeralEnvironmentsSettingsPageData;
}
export function EphemeralEnvironmentsSettingsPage({ project: initialProject, loaderData }: EphemeralEnvironmentsSettingsPageProps) {
    const ephemeralEnvironmentsEnabled = useEphemeralEnvironmentsEnabled();
    const startEditParentEnvironmentWorkflow = useWorkflow(workflows.editParentEnvironmentWorkflow);
    const startEditChannelWorkflow = useWorkflow(workflows.editChannelWorkflow);
    const projectContext = useProjectContext();
    const { model: project, setModel: setProject, formProps, createSaveAction, } = useForm({
        formName: "Ephemeral environments settings",
        initialModel: initialProject,
        onSubmit: async (repository, model) => await repository.Projects.modify(model),
        updateFormModelAfterSubmit: (model) => model,
        afterSubmit: async () => {
            await projectContext.actions.refreshModel();
        },
        submitPermission: { permission: Permission.ProjectEdit, project: initialProject.Id, tenant: "*" },
    });
    const { result: runbooks, refetch: reloadRunbooks } = useQuery(async (repository) => {
        // useForm doesn't expose a clean model, so if we use project here, we refresh
        // the Runbooks every time we change the Runbook in one of the dropdowns. We don't
        // want that, so we're using initialProject here.
        //
        // It won't get updated after a save, but there is nothing in getRunbooksForProject
        // that can be impacted by anything that we can change on this page, so that's fine.
        return await getRunbooksForProject(repository, initialProject);
    }, [initialProject], "Load Runbooks", { initialResult: loaderData.configured ? loaderData.runbooks : null });
    const { result: channel, refetch: reloadChannel } = useQuery(async (repository) => {
        if (project) {
            return await getEphemeralEnvironmentChannel(repository, project);
        }
    }, [project], "Load Channel", { initialResult: loaderData.configured ? loaderData.channel : null });
    const { result: parentEnvironment, refetch: reloadParentEnvironment } = useQuery(async (repository) => {
        if (channel) {
            return await getParentEnvironment(repository, channel);
        }
    }, [channel], "Load Parent Environment", { initialResult: loaderData.configured ? loaderData.parentEnvironment : null });
    if (!ephemeralEnvironmentsEnabled || !loaderData.configured || !runbooks || !parentEnvironment || !channel) {
        return <NotFound />;
    }
    const provisioningRunbook = findRunbook(project.ProvisioningRunbookId, runbooks);
    const deprovisioningRunbook = findRunbook(project.DeprovisioningRunbookId, runbooks);
    const header = {
        ...ephemeralEnvironmentsHeader(),
        primaryAction: createSaveAction(),
    };
    return (<PageContent header={header} legacyTabs={<EphemeralEnvironmentsTabs project={project} currentTab="settings"/>}>
            <Section>
                Learn how ephemeral environments use their parent environment, dedicated channel, and environment name template in our <ExternalLink href="ephemeralenvironments">documentation</ExternalLink>.
            </Section>
            <Form {...formProps}>
                <ExpandableFormSection errorKey="parentEnvironment" title="Parent Environment" summary={Summary.summary(parentEnvironment.Name)} help="Ephemeral environments are created based on a parent environment, inheriting its scoped deployment targets, variables, and accounts." contextualHelp={<ParentEnvironmentInfoPopover />} isExpandedByDefault>
                    <ParentEnvironmentConfigurationCard parentEnvironment={parentEnvironment} onClick={() => startEditParentEnvironmentWorkflow({ parentEnvironment: parentEnvironment }, reloadParentEnvironment)}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="channel" title="Ephemeral Environments Channel" summary={Summary.summary(channel.Name)} help="Releases in this channel will provision a new ephemeral environment and deploy to it automatically." contextualHelp={<EphemeralEnvironmentChannelInfoPopover />} isExpandedByDefault>
                    <ChannelConfigurationCard channel={channel} parentEnvironment={parentEnvironment} onClick={() => startEditChannelWorkflow({ project, channel, lookupData: loaderData.channelLookupData, gitRefValidationError: projectContext.state.gitRefValidationError, gitRef: projectContext.state.gitRef }, reloadChannel)}/>
                </ExpandableFormSection>
                <SectionHeader title="Runbooks"/>
                <ExpandableFormSection errorKey="provisioningRunbook" title="Provisioning runbook" summary={getRunbookSummary(provisioningRunbook)} help="Runbook used to provision ephemeral environments.">
                    <MissingRunbookCallout project={project} runbook={provisioningRunbook}/>
                    <SelectWithAddRefresh value={project.ProvisioningRunbookId} onChange={(id) => setProject((prev) => ({ ...prev, ProvisioningRunbookId: id }))} addUrl={links.runbooksPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug })} onRequestRefresh={async () => reloadRunbooks()} items={runbooks.map((r) => ({ value: r.Id, text: r.Name }))} label="Provisioning runbook" sortItems allowClear/>
                    <RunbookLink project={project} runbook={provisioningRunbook}/>
                </ExpandableFormSection>
                <ExpandableFormSection errorKey="deprovisioningRunbook" title="Deprovisioning runbook" summary={getRunbookSummary(deprovisioningRunbook)} help="Runbook used to deprovision ephemeral environments.">
                    <MissingRunbookCallout project={project} runbook={deprovisioningRunbook}/>
                    <SelectWithAddRefresh value={project.DeprovisioningRunbookId} onChange={(id) => setProject((prev) => ({ ...prev, DeprovisioningRunbookId: id }))} addUrl={links.runbooksPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug })} onRequestRefresh={async () => reloadRunbooks()} items={runbooks.map((r) => ({ value: r.Id, text: r.Name }))} label="Deprovisioning runbook" sortItems allowClear/>
                    <RunbookLink project={project} runbook={deprovisioningRunbook}/>
                </ExpandableFormSection>
            </Form>
        </PageContent>);
}
interface MissingRunbookCalloutProps {
    project: ProjectResource;
    runbook: FindRunbookResult;
}
function MissingRunbookCallout({ project, runbook }: MissingRunbookCalloutProps) {
    if (runbook.missing) {
        if (HasRunbooksInGit(project.PersistenceSettings)) {
            return <Callout type="warning" title={`Could not find a runbook with file name '${runbook.id}.ocl' on the default branch in your project.`}/>;
        }
        else {
            return <Callout type="warning" title={`Could not find a runbook with ID '${runbook.id}' in your project.`}/>;
        }
    }
    return null;
}
interface RunbookLinkProps {
    project: ProjectResource;
    runbook: FindRunbookResult;
}
function RunbookLink({ project, runbook }: RunbookLinkProps) {
    if (!runbook.selected || runbook.missing) {
        return null;
    }
    const gitRef = HasGitPersistenceSettings(project.PersistenceSettings) && HasRunbooksInGit(project.PersistenceSettings) ? project.PersistenceSettings.DefaultBranch : undefined;
    return <Button icon={<RunbooksIcon size={16}/>} importance="quiet" href={links.runbookRunsPage.generateUrl({ spaceId: project.SpaceId, projectSlug: project.Slug, runbookId: runbook.id }, { gitRef })} label="View Runbook"/>;
}
function getRunbookSummary(runbook: FindRunbookResult) {
    if (!runbook.selected) {
        return Summary.summary("None selected");
    }
    if (runbook.missing) {
        return Summary.summary(`Runbook '${runbook.id}' not found`);
    }
    return Summary.summary(runbook.runbook.Name);
}
interface FindRunbookNotSelected {
    missing: false;
    selected: false;
}
interface FindRunbookExists {
    id: string;
    runbook: RunbookResource;
    missing: false;
    selected: true;
}
interface FindRunbookMissing {
    id: string;
    missing: true;
    selected: true;
}
type FindRunbookResult = FindRunbookNotSelected | FindRunbookExists | FindRunbookMissing;
function findRunbook(id: string | undefined, runbooks: RunbookResource[]): FindRunbookResult {
    if (!id) {
        return {
            missing: false,
            selected: false,
        };
    }
    const runbook = runbooks.find((runbook) => runbook.Id === id);
    if (runbook) {
        return {
            id,
            runbook,
            missing: false,
            selected: true,
        };
    }
    return {
        id,
        missing: true,
        selected: true,
    };
}
